/**
 * Redux async thunks for Voice Leads admin dashboard.
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../helpers/axiosconfig';

const toVoiceLeadFailure = (error, fallback) => {
  const status = Number(error?.response?.status) || null;
  const networkCodes = new Set(['ERR_NETWORK', 'ECONNABORTED', 'ENETUNREACH', 'ETIMEDOUT']);
  const kind = !error?.response || networkCodes.has(error?.code)
    ? 'offline'
    : status === 401
      ? 'unauthorized'
      : status === 403
        ? 'forbidden'
        : status === 404
          ? 'notFound'
          : status === 409
            ? 'conflict'
            : status >= 500
              ? 'server'
              : 'fallback';

  return { error: fallback, status, kind };
};

export const fetchVoiceLeads = createAsyncThunk(
  'voiceLeads/fetchAll',
  async ({ status, page = 1, limit = 50 } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      params.set('page', String(page));
      params.set('limit', String(limit));
      const response = await axios.get(`v1/admin/voice-leads?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(toVoiceLeadFailure(error, 'Failed to fetch leads'));
    }
  }
);

export const fetchVoiceLeadStats = createAsyncThunk(
  'voiceLeads/fetchStats',
  async ({ days } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (days) params.set('days', String(days));
      const suffix = params.toString() ? `?${params.toString()}` : '';
      const response = await axios.get(`v1/admin/voice-leads/stats${suffix}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(toVoiceLeadFailure(error, 'Failed to fetch stats'));
    }
  }
);

export const fetchVoiceLeadQuality = createAsyncThunk(
  'voiceLeads/fetchQuality',
  async ({ days = 30 } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.set('days', String(days));
      const response = await axios.get(`v1/admin/voice-leads/quality?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(toVoiceLeadFailure(error, 'Failed to fetch quality metrics'));
    }
  }
);

export const fetchVoiceLeadDetail = createAsyncThunk(
  'voiceLeads/fetchDetail',
  async (leadId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`v1/admin/voice-leads/${leadId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(toVoiceLeadFailure(error, 'Failed to fetch lead detail'));
    }
  }
);

export const fetchVoiceLeadEvents = createAsyncThunk(
  'voiceLeads/fetchEvents',
  async (leadId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`v1/admin/voice-leads/${leadId}/events`);
      return response.data;
    } catch (error) {
      return rejectWithValue(toVoiceLeadFailure(error, 'Failed to fetch events'));
    }
  }
);

export const triggerVoiceLeadRetry = createAsyncThunk(
  'voiceLeads/triggerRetry',
  async (leadId, { rejectWithValue }) => {
    try {
      const response = await axios.post(`v1/admin/voice-leads/${leadId}/retry`);
      return response.data;
    } catch (error) {
      return rejectWithValue(toVoiceLeadFailure(error, 'Failed to trigger retry'));
    }
  }
);
